/**
 * NotificationList Component
 *
 * Grouped list of notifications organized by priority.
 * Also shows system notifications (toast messages) when filtered.
 */

import { useMemo } from 'react';
import { Bell, AlertTriangle, AlertCircle, Info, CheckCircle, XCircle, Terminal } from 'lucide-react';
import { formatDistanceToNowStrict } from 'date-fns';
import { useNotificationContext } from '@/context/NotificationContext';
import { NotificationItem } from './NotificationItem';
import { cn } from '@/lib/utils';
import type { Notification, NotificationPriority, SystemNotification } from '@/types/notifications';

interface PriorityGroupConfig {
  priority: NotificationPriority;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}

const PRIORITY_GROUPS: PriorityGroupConfig[] = [
  { priority: 'urgent', label: 'Urgent', icon: AlertTriangle, color: 'text-red-500' },
  { priority: 'action_required', label: 'Action Required', icon: AlertCircle, color: 'text-amber-500' },
  { priority: 'info', label: 'Recent', icon: Info, color: 'text-blue-500' },
];

// System notification item component
function SystemNotificationItem({ notification }: { notification: SystemNotification }) {
  const getTypeConfig = (type: SystemNotification['type']) => {
    switch (type) {
      case 'success':
        return {
          icon: CheckCircle,
          color: 'text-green-600 dark:text-green-400',
          bgColor: 'bg-green-50 dark:bg-green-900/20',
          borderColor: 'border-green-200 dark:border-green-800',
        };
      case 'error':
        return {
          icon: XCircle,
          color: 'text-red-600 dark:text-red-400',
          bgColor: 'bg-red-50 dark:bg-red-900/20',
          borderColor: 'border-red-200 dark:border-red-800',
        };
      case 'warning':
        return {
          icon: AlertTriangle,
          color: 'text-amber-600 dark:text-amber-400',
          bgColor: 'bg-amber-50 dark:bg-amber-900/20',
          borderColor: 'border-amber-200 dark:border-amber-800',
        };
      default:
        return {
          icon: Info,
          color: 'text-blue-600 dark:text-blue-400',
          bgColor: 'bg-blue-50 dark:bg-blue-900/20',
          borderColor: 'border-blue-200 dark:border-blue-800',
        };
    }
  };

  const config = getTypeConfig(notification.type);
  const Icon = config.icon;

  return (
    <div
      className={cn(
        'relative border rounded-md px-2 py-1.5',
        config.bgColor,
        config.borderColor
      )}
    >
      <div className="flex items-start gap-1.5">
        <Icon className={cn('h-3 w-3 mt-0.5 shrink-0', config.color)} />
        <div className="flex-1 min-w-0">
          {/* Header row */}
          <div className="flex items-center justify-between gap-1 mb-0.5">
            {notification.category && (
              <span className="text-[9px] uppercase tracking-wide text-muted-foreground font-medium">
                {notification.category}
              </span>
            )}
            <span className="text-[9px] text-muted-foreground/60">
              {notification.businessName && <span className="mr-1">{notification.businessName} •</span>}
              {formatDistanceToNowStrict(notification.timestamp, { addSuffix: true })}
            </span>
          </div>
          <h4 className={cn('font-medium text-xs leading-tight', config.color)}>
            {notification.title}
          </h4>
          {notification.description && (
            <p className="text-[10px] text-muted-foreground mt-0.5 leading-snug line-clamp-1">
              {notification.description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export function NotificationList() {
  const { notifications, isLoading, filter, systemNotifications } = useNotificationContext();

  // Group notifications by priority (only when showing 'all')
  const groupedNotifications = useMemo(() => {
    if (filter !== 'all' && filter !== 'system') {
      // When filtered by priority, show as flat list
      return new Map<NotificationPriority, Notification[]>([
        [filter as NotificationPriority, notifications]
      ]);
    }

    // Group by priority
    const groups = new Map<NotificationPriority, Notification[]>();

    notifications.forEach(notification => {
      const existing = groups.get(notification.priority) || [];
      groups.set(notification.priority, [...existing, notification]);
    });

    return groups;
  }, [notifications, filter]);

  if (isLoading && filter !== 'system') {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  // Show system notifications when filter is 'system'
  if (filter === 'system') {
    if (systemNotifications.length === 0) {
      return (
        <div className="text-center py-8 px-4">
          <Terminal className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-muted-foreground text-xs font-medium">
            No system notifications
          </p>
          <p className="text-muted-foreground/70 text-[10px] mt-0.5">
            Toast messages will appear here
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-1.5">
        {systemNotifications.map(notification => (
          <SystemNotificationItem
            key={notification.id}
            notification={notification}
          />
        ))}
      </div>
    );
  }

  if (notifications.length === 0 && (filter !== 'all' || systemNotifications.length === 0)) {
    return (
      <div className="text-center py-8 px-4">
        <Bell className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
        <p className="text-muted-foreground text-xs font-medium">
          {filter === 'all' ? 'No notifications' : `No ${filter.replace('_', ' ')} notifications`}
        </p>
        <p className="text-muted-foreground/70 text-[10px] mt-0.5">
          You're all caught up!
        </p>
      </div>
    );
  }

  // When showing all, display grouped by priority + system notifications
  if (filter === 'all') {
    return (
      <div className="space-y-3">
        {/* System notifications section (if any) */}
        {systemNotifications.length > 0 && (
          <div>
            <div className="flex items-center gap-1.5 mb-1.5 px-1">
              <Terminal className="h-3 w-3 text-muted-foreground" />
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                System
              </span>
              <span className="text-[10px] text-muted-foreground/60">
                ({systemNotifications.length})
              </span>
            </div>
            <div className="space-y-1.5">
              {systemNotifications.slice(0, 3).map(notification => (
                <SystemNotificationItem
                  key={notification.id}
                  notification={notification}
                />
              ))}
              {systemNotifications.length > 3 && (
                <p className="text-[10px] text-muted-foreground text-center py-0.5">
                  +{systemNotifications.length - 3} more in System tab
                </p>
              )}
            </div>
          </div>
        )}

        {/* DB notifications grouped by priority */}
        {PRIORITY_GROUPS.map(({ priority, label, icon: Icon, color }) => {
          const group = groupedNotifications.get(priority);
          if (!group || group.length === 0) return null;

          return (
            <div key={priority}>
              {/* Group header */}
              <div className="flex items-center gap-1.5 mb-1.5 px-1">
                <Icon className={cn('h-3 w-3', color)} />
                <span className={cn('text-[10px] font-semibold uppercase tracking-wide', color)}>
                  {label}
                </span>
                <span className="text-[10px] text-muted-foreground/60">
                  ({group.length})
                </span>
              </div>

              {/* Notifications */}
              <div className="space-y-1.5">
                {group.map(notification => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // When filtered, show flat list
  return (
    <div className="space-y-1.5">
      {notifications.map(notification => (
        <NotificationItem
          key={notification.id}
          notification={notification}
        />
      ))}
    </div>
  );
}
